(() => {
  if (window.ohProductPreviewReady || !window.HTMLDialogElement?.prototype.showModal) return;
  window.ohProductPreviewReady = true;
  let active, sequence = 0, cancelRestore, keyboardFocus = false;
  document.addEventListener('pointerdown', () => {keyboardFocus = false;}, true);
  document.addEventListener('keydown', event => {
    if (['Shift','Control','Alt','Meta'].includes(event.key)) return;
    keyboardFocus = true;
    document.querySelectorAll('[data-oh-pointer-focus]').forEach(element => element.removeAttribute('data-oh-pointer-focus'));
  }, true);
  function returnFocus(element) {
    // Keep the logical focus destination for assistive technology and Tab,
    // without turning pointer-driven modal return into a keyboard focus ring.
    element.toggleAttribute('data-oh-pointer-focus', !keyboardFocus);
    element.focus({preventScroll:true});
    element.addEventListener('blur', () => element.removeAttribute('data-oh-pointer-focus'), {once:true});
  }
  const returnKey = 'ohProductPreviewReturn';
  const ordinary = event => !event.defaultPrevented && event.button === 0 && !event.metaKey && !event.ctrlKey && !event.shiftKey && !event.altKey;
  function clearReturn() {
    if (!history.state?.[returnKey]) return;
    const next = {...history.state}; delete next[returnKey];
    history.replaceState(next, '', location.href);
  }
  const markCurrent = (link, current) => {
    if (current) link.setAttribute('aria-current', 'true');
    else link.removeAttribute('aria-current');
  };
  const node = (tag, className, text) => {
    const element = document.createElement(tag);
    if (className) element.className = className;
    if (text !== undefined) element.textContent = text;
    return element;
  };
  const imageKey = value => {
    const url = new URL(value, location.href);
    return url.origin + url.pathname.replace(/-(?:120|320|600|640|1200|1280|1920)(\.[a-z]+)$/i, '$1');
  };
  function photoArrows(parent, navigate) {
    const buttons = [-1, 1].map(direction => {
      const button = node('button', `oh-photo-arrow oh-photo-arrow--${direction < 0 ? 'previous' : 'next'}`);
      button.type = 'button'; button.setAttribute('aria-label', direction < 0 ? 'Previous photo' : 'Next photo');
      button.hidden = true;
      button.addEventListener('click', () => navigate(direction));
      parent.append(button);
      return button;
    });
    return (index, count) => {
      buttons[0].hidden = index <= 0;
      buttons[1].hidden = index < 0 || index >= count - 1;
      const focused = buttons.find(button => button === document.activeElement && button.hidden);
      if (focused) buttons.find(button => !button.hidden)?.focus({preventScroll:true});
    };
  }
  function trapFocus(dialog, event) {
    if (event.key !== 'Tab' || event.target.closest('dialog') !== dialog) return;
    const controls = [...dialog.querySelectorAll('a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), summary, [tabindex]:not([tabindex="-1"])')]
      .filter(element => element.getClientRects().length && !element.closest('[hidden]'));
    const destination = event.shiftKey && document.activeElement === controls[0] ? controls.at(-1)
      : !event.shiftKey && document.activeElement === controls.at(-1) ? controls[0] : null;
    if (destination) {event.preventDefault(); destination.focus();}
  }
  function enlargePhoto(state, photo, opener) {
    if (!photo) return;
    state.imageClose?.();
    const view = node('dialog', 'oh-product-image-view');
    view.setAttribute('aria-label', 'Enlarged product photo');
    const dismiss = node('button', 'oh-product-preview__close');
    dismiss.type = 'button'; dismiss.setAttribute('aria-label', 'Close enlarged photo');
    const closeMark = node('span', 'oh-product-image-view__close-mark');
    closeMark.setAttribute('aria-hidden', 'true'); dismiss.append(closeMark);
    const viewport = node('div', 'oh-product-image-view__viewport');
    const canvas = node('div', 'oh-product-image-view__canvas');
    const zoom = node('button', 'oh-product-image-view__photo');
    zoom.type = 'button'; zoom.setAttribute('aria-label', 'Zoom photo');
    zoom.setAttribute('aria-pressed', 'false');
    const image = node('img', 'oh-product-image-view__image');
    // Use an existing full-size gallery URL when the card uses its smaller copy.
    const original = [...state.dialog.querySelectorAll('[data-native-photo]')].find(link => imageKey(link.href) === imageKey(photo.src));
    image.src = original?.href || photo.src;
    image.alt = photo.alt;
    image.draggable = false;
    zoom.append(image); canvas.append(zoom); viewport.append(canvas); view.append(viewport, dismiss);
    state.dialog.append(view);
    document.documentElement.classList.add('oh-product-image-open');
    let enlarged = false;
    const updateArrows = photoArrows(view, direction => {
      if (!state.navigatePhoto(direction)) return;
      enlarged = false;
      view.classList.remove('is-zoomed'); zoom.setAttribute('aria-pressed', 'false');
      image.src = photo.src; image.alt = photo.alt;
      sizeImage();
    });
    function positionClose() {
      const bounds = zoom.getBoundingClientRect(), area = viewport.getBoundingClientRect(), parent = view.getBoundingClientRect();
      // Pin to the visible image corner, keeping the close control reachable
      // while the magnified photo is panned beyond the viewport.
      dismiss.style.left = `${Math.max(0, Math.min(view.clientWidth - 44, Math.min(bounds.right, area.right) - parent.left - 48))}px`;
      dismiss.style.top = `${Math.max(0, Math.min(view.clientHeight - 44, Math.max(bounds.top, area.top) - parent.top + 4))}px`;
      const top = Math.max(bounds.top, area.top), bottom = Math.min(bounds.bottom, area.bottom);
      view.querySelectorAll('.oh-photo-arrow').forEach(button => {
        const left = button.classList.contains('oh-photo-arrow--previous')
          ? Math.max(bounds.left, area.left) + 4
          : Math.min(bounds.right, area.right) - 48;
        button.style.left = `${left - parent.left}px`;
        button.style.top = `${(top + bottom) / 2 - parent.top - 22}px`;
      });
      const {index, count} = state.photoPosition(); updateArrows(index, count);
    }
    function sizeImage(point = {x:.5, y:.5}) {
      const ratio = (image.naturalWidth || photo.naturalWidth || 1) / (image.naturalHeight || photo.naturalHeight || 1);
      const fittedWidth = Math.min(viewport.clientWidth, viewport.clientHeight * ratio);
      const width = fittedWidth * (enlarged ? 2 : 1), height = width / ratio;
      canvas.style.width = `${Math.max(viewport.clientWidth, width)}px`;
      canvas.style.height = `${Math.max(viewport.clientHeight, height)}px`;
      zoom.style.width = `${width}px`; zoom.style.height = `${height}px`;
      viewport.scrollTo({
        left: enlarged ? zoom.offsetLeft + point.x * width - viewport.clientWidth / 2 : 0,
        top: enlarged ? zoom.offsetTop + point.y * height - viewport.clientHeight / 2 : 0,
        behavior:'instant'
      });
      positionClose();
    }
    const resize = () => sizeImage();
    const closeImage = () => {
      window.removeEventListener('resize', resize);
      view.remove();
      document.documentElement.classList.remove('oh-product-image-open');
      state.imageClose = null;
      if (opener.isConnected) opener.focus({preventScroll:true});
    };
    const toggleZoom = event => {
      const bounds = zoom.getBoundingClientRect();
      const point = event.detail ? {
        x: Math.min(1, Math.max(0, (event.clientX - bounds.left) / bounds.width)),
        y: Math.min(1, Math.max(0, (event.clientY - bounds.top) / bounds.height))
      } : {x:.5, y:.5};
      enlarged = !enlarged;
      view.classList.toggle('is-zoomed', enlarged);
      zoom.setAttribute('aria-pressed', String(enlarged));
      sizeImage(point);
    };
    state.imageClose = closeImage;
    zoom.addEventListener('click', toggleZoom);
    viewport.addEventListener('scroll', positionClose, {passive:true});
    dismiss.addEventListener('click', closeImage);
    view.addEventListener('cancel', event => {event.preventDefault(); event.stopPropagation(); closeImage();});
    view.addEventListener('click', event => {if (event.target === view || event.target === canvas) closeImage();});
    view.addEventListener('keydown', event => trapFocus(view, event));
    zoom.addEventListener('keydown', event => {
      if (!enlarged || !['ArrowLeft','ArrowRight','ArrowUp','ArrowDown'].includes(event.key)) return;
      event.preventDefault();
      viewport.scrollBy({left:event.key==='ArrowLeft'?-40:event.key==='ArrowRight'?40:0, top:event.key==='ArrowUp'?-40:event.key==='ArrowDown'?40:0, behavior:'instant'});
    });
    image.addEventListener('load', resize);
    window.addEventListener('resize', resize);
    view.showModal(); sizeImage(); dismiss.focus({preventScroll:true});
  }
  function setupProductGallery(root) {
    const gallery = root.querySelector('.oh-product-page .oh-shop-gallery');
    const photo = gallery?.querySelector('.oh-shop-photo img');
    if (!photo || gallery.dataset.previewGalleryReady) return;
    gallery.dataset.previewGalleryReady = 'true';
    const enlarge = node('button', 'oh-product-preview__enlarge');
    enlarge.type = 'button'; enlarge.setAttribute('aria-label', 'Enlarge product photo');
    photo.replaceWith(enlarge); enlarge.append(photo);
    const links = [...gallery.querySelectorAll('[data-native-photo]')];
    const state = {dialog:root};
    state.photoPosition = () => ({index:links.findIndex(link => imageKey(link.href) === imageKey(photo.src)), count:links.length});
    const selectPhoto = link => {
      photo.src = link.href; photo.alt = link.dataset.alt || photo.alt;
      photo.toggleAttribute('data-supplier-reference', link.href.includes('oh-xs-reference-'));
    };
    state.navigatePhoto = direction => {
      const {index} = state.photoPosition(), link = links[index + direction];
      if (index < 0 || !link) return false;
      selectPhoto(link); return true;
    };
    const arrows = photoArrows(enlarge.parentElement, state.navigatePhoto);
    const refresh = () => {
      const {index,count} = state.photoPosition();
      links.forEach((link,n) => markCurrent(link, n === index)); arrows(index,count);
    };
    // Existing variant/gallery handlers remain authoritative. Observe the actual
    // photo so arrows also track selections made by native or preview controls.
    new MutationObserver(refresh).observe(photo,{attributes:true,attributeFilter:['src']});
    links.forEach(link => link.addEventListener('click', event => {event.preventDefault(); selectPhoto(link);}));
    enlarge.addEventListener('click', () => enlargePhoto(state,photo,enlarge));
    refresh();
  }
  function brandMark(source, prefix) {
    if (!source) return null;
    const copy = source.cloneNode(true);
    const identifiers = new Map();
    copy.querySelectorAll('[id]').forEach((element, index) => {
      const replacement = `${prefix}-logo-${index}`;
      identifiers.set(element.id, replacement);
      element.id = replacement;
    });
    // Original brand artwork uses SVG filters and clipping paths. Give its
    // cloned definitions unique IDs while keeping every reference intact.
    copy.querySelectorAll('*').forEach(element => {
      for (const attribute of [...element.attributes]) {
        let value = attribute.value;
        for (const [original, replacement] of identifiers) {
          value = value.replaceAll(`url(#${original})`, `url(#${replacement})`);
          if (value === `#${original}`) value = `#${replacement}`;
        }
        if (value !== attribute.value) element.setAttribute(attribute.name, value);
      }
    });
    copy.querySelectorAll('img').forEach(image => image.loading = 'eager');
    return copy;
  }
  // Copy presentation only. Fetched product-page scripts and forms must not run
  // alongside the card's existing, authoritative cart form.
  function presentation(source) {
    if (!source) return null;
    const copy = source.cloneNode(true);
    copy.querySelectorAll('script,style,iframe,object,embed,form,template,input,button').forEach(element => element.remove());
    for (const element of [copy, ...copy.querySelectorAll('*')]) {
      element.removeAttribute('id');
      // Variant handlers must keep updating the original card photo; the modal
      // refresh handles its own gallery without becoming the card's main image.
      element.removeAttribute('data-native-main-image');
      for (const attribute of [...element.attributes]) {
        if (/^on/i.test(attribute.name)) element.removeAttribute(attribute.name);
        if (['src', 'href'].includes(attribute.name)) {
          const url = new URL(attribute.value, location.href);
          if (!['http:', 'https:', 'mailto:', 'tel:'].includes(url.protocol)) element.removeAttribute(attribute.name);
        }
      }
    }
    return copy;
  }
  function close() {
    cancelRestore?.();
    clearReturn();
    const state = active;
    if (!state) return;
    active = null;
    state.imageClose?.();
    state.controller.abort();
    state.observer.disconnect();
    state.form?.removeEventListener('change', state.refresh);
    if (state.spacer?.isConnected) state.spacer.replaceWith(state.form);
    state.dialog.remove();
    document.documentElement.classList.remove('oh-product-preview-open');
    window.scrollTo({top: state.scroll, behavior: 'instant'});
    if (state.opener.isConnected) returnFocus(state.opener);
  }
  async function open(card, opener, href, restored) {
    close();
    const productUrl = new URL(href, location.href);
    if (productUrl.origin !== location.origin) return;
    const form = card.querySelector('.oh-quick-buy');
    const select = form?.querySelector('select');
    if (select?.value && select.value !== 'custom') productUrl.searchParams.set('variant', select.value);
    const dialog = node('dialog', 'oh-product-preview');
    const headingId = `oh-product-preview-title-${++sequence}`;
    const brandName = (card.dataset.brand || card.querySelector('.oh-eyebrow')?.textContent || '').trim();
    dialog.setAttribute('aria-labelledby', headingId);
    const header = node('header', 'oh-product-preview__header');
    header.append(node('span', '', brandName ? `${brandName} Product Details` : 'Product Details'));
    const dismiss = node('button', 'oh-product-preview__close');
    dismiss.type = 'button'; dismiss.setAttribute('aria-label', 'Close product details');
    const dismissMark = node('span', 'oh-product-preview__close-symbol');
    dismissMark.setAttribute('aria-hidden', 'true'); dismiss.append(dismissMark);
    header.append(dismiss);
    const body = node('div', 'oh-product-preview__body');
    const gallery = node('div', 'oh-product-preview__gallery oh-product-preview__card');
    const info = node('div', 'oh-product-preview__info');
    const purchase = node('section', 'oh-product-preview__purchase oh-product-preview__card');
    purchase.setAttribute('aria-labelledby', headingId);
    const brand = brandMark(card.querySelector('.oh-equipment-brand'), headingId)
      || node('p', 'oh-eyebrow', brandName);
    const title = node('h2', '', card.querySelector('h3')?.textContent || 'Product Details');
    title.id = headingId;
    const price = node('p', 'oh-shop-card__price');
    const status = node('p', 'oh-shop-note', 'Loading product details…');
    status.setAttribute('role', 'status');
    const description = node('div', 'oh-shop-description');
    const options = node('div', 'oh-product-preview__options');
    const item = node('p', 'oh-shop-note oh-product-preview__item');
    item.setAttribute('data-preview-sku', '');
    const specifications = node('div', 'oh-shop-disclosures');
    const fullPage = node('a', 'oh-text-link', 'View Full Product Details →');
    fullPage.href = productUrl.href;
    purchase.append(brand, title, price, status, description, item, options);
    info.append(purchase, specifications, fullPage);
    body.append(gallery, info); dialog.append(header, body);
    const scroll = restored?.scroll ?? window.scrollY;
    let spacer;
    if (form) {
      spacer = node('div', 'oh-product-preview__spacer');
      spacer.style.height = `${form.getBoundingClientRect().height}px`;
      form.replaceWith(spacer);
      options.append(form);
    } else {
      const choose = node('a', 'oh-button', 'Choose options on product page');
      choose.href = productUrl.href; options.append(choose);
    }
    // Keep the form inside its original card and grid. Both native cart listeners
    // and the local preview's delegated handlers continue to use the same form.
    card.append(dialog);
    const controller = new AbortController();
    const refresh = () => {
      const sourcePrice = card.querySelector('[data-quick-price], .oh-shop-card > .oh-shop-card__price');
      price.replaceChildren(...(sourcePrice ? [...sourcePrice.childNodes].map(child => child.cloneNode(true)) : []));
      const option = select?.selectedOptions[0];
      item.replaceChildren();
      if (option?.dataset.sku) item.append(node('strong', 'oh-item-label', 'Item:'), document.createTextNode(` ${option.dataset.sku}`));
      item.hidden = !item.textContent;
      if (option?.value && option.value !== 'custom') fullPage.href = `${productUrl.pathname}?variant=${encodeURIComponent(option.value)}`;
      const photo = gallery.querySelector('.oh-shop-photo img');
      const source = option?.dataset.image || card.querySelector('.oh-shop-card__image > img')?.src;
      if (photo && source) { photo.src = source; photo.alt = option?.dataset.alt || title.textContent; }
      if (source) gallery.querySelectorAll('[data-native-photo]').forEach(link => markCurrent(link, imageKey(link.href) === imageKey(source)));
      updateGalleryArrows();
    };
    const observer = new MutationObserver(refresh);
    const cardPrice = card.querySelector('[data-quick-price]') || card.querySelector('.oh-shop-card > .oh-shop-card__price');
    if (cardPrice) observer.observe(cardPrice, {childList: true, characterData: true, subtree: true});
    const state = {dialog, opener, form, spacer, controller, observer, refresh, scroll};
    state.photoPosition = () => {
      const links = [...gallery.querySelectorAll('[data-native-photo]')];
      const source = gallery.querySelector('.oh-shop-photo img')?.src;
      return {index: source ? links.findIndex(link => imageKey(link.href) === imageKey(source)) : -1, count:links.length};
    };
    let galleryArrows;
    const updateGalleryArrows = () => {
      const {index, count} = state.photoPosition(); galleryArrows?.(index, count);
    };
    const showPhoto = thumbnail => {
      const photo = gallery.querySelector('.oh-shop-photo img');
      if (photo) {photo.src = thumbnail.href; photo.alt = thumbnail.dataset.alt || title.textContent;}
      gallery.querySelectorAll('[data-native-photo]').forEach(link => markCurrent(link, link === thumbnail));
      updateGalleryArrows();
    };
    state.navigatePhoto = direction => {
      const {index} = state.photoPosition();
      const target = [...gallery.querySelectorAll('[data-native-photo]')][index + direction];
      if (index < 0 || !target) return false;
      showPhoto(target); return true;
    };
    active = state;
    form?.addEventListener('change', refresh);
    dismiss.addEventListener('click', close);
    dialog.addEventListener('cancel', event => {event.preventDefault(); close();});
    dialog.addEventListener('click', event => {if (event.target === dialog) close();});
    dialog.addEventListener('click', event => {
      const link = event.target.closest('a[href]');
      if (!link || !ordinary(event) || link.download || link.target && link.target !== '_self') return;
      const target = new URL(link.href, location.href);
      if (target.origin !== location.origin || !target.pathname.startsWith('/products/')) return;
      const variant = target.searchParams.get('variant');
      const option = [...(select?.options || [])].find(option => option.value === variant && !option.disabled);
      if (specifications.contains(link) && target.pathname === productUrl.pathname && variant && option) {
        event.preventDefault(); select.value = variant;
        select.dispatchEvent(new Event('change', {bubbles:true}));
        return;
      }
      history.replaceState({...history.state, [returnKey]: {
        source:location.href, product:productUrl.pathname, variant:select?.value,
        keyboard:keyboardFocus,
        scroll:state.scroll,
        photo:gallery.querySelector('.oh-shop-photo img')?.src,
        disclosures:[...specifications.querySelectorAll('details')].map(detail => detail.open)
      }}, '', location.href);
    });
    dialog.addEventListener('keydown', event => trapFocus(dialog, event));
    gallery.addEventListener('click', event => {
      const enlarge = event.target.closest('.oh-product-preview__enlarge');
      if (enlarge) {enlargePhoto(state, enlarge.querySelector('img'), enlarge); return;}
      const thumbnail = event.target.closest('[data-native-photo]');
      if (!thumbnail) return;
      event.preventDefault();
      showPhoto(thumbnail);
    });
    document.documentElement.classList.add('oh-product-preview-open');
    if (restored) keyboardFocus = !!restored.keyboard;
    dialog.showModal(); returnFocus(dismiss); refresh();
    try {
      const response = await fetch(productUrl.href, {signal: controller.signal, headers: {Accept: 'text/html'}});
      if (!response.ok) throw Error('Product details unavailable');
      const page = new DOMParser().parseFromString(await response.text(), 'text/html');
      const product = page.querySelector('#equipment-product');
      if (!product) throw Error('Product details unavailable');
      if (active !== state) return;
      const media = presentation(product.querySelector('.oh-shop-gallery'));
      const text = presentation(product.querySelector('.oh-shop-description'));
      const quote = presentation(product.querySelector('.oh-delivery-quote'));
      const details = presentation(product.querySelector('.oh-shop-disclosures'));
      if (media) gallery.append(media);
      const mainPhoto = gallery.querySelector('.oh-shop-photo img');
      if (mainPhoto) {
        const enlarge = node('button', 'oh-product-preview__enlarge');
        enlarge.type = 'button'; enlarge.setAttribute('aria-label', 'Enlarge product photo');
        mainPhoto.replaceWith(enlarge); enlarge.append(mainPhoto);
        galleryArrows = photoArrows(enlarge.parentElement, state.navigatePhoto);
        updateGalleryArrows();
      }
      if (text) description.append(...text.childNodes);
      if (!form && quote) options.replaceChildren(quote);
      if (details) {
        details.querySelectorAll(':scope > details').forEach(element => {
          element.open = !element.querySelector('summary')?.textContent.trim().startsWith('All Options & Prices');
          element.classList.add('oh-product-preview__card');
          const summary = element.querySelector('summary');
          if (summary) {
            const label = node('span', 'oh-product-preview__spec-label');
            [...summary.childNodes].filter(child => child.nodeType === Node.TEXT_NODE).forEach(child => label.append(child));
            summary.prepend(label);
          }
        });
        // Charts stay in Fit & Setup below the purchase controls. Initialize
        // their unit switch locally without copying purchase/measurement forms.
        details.querySelectorAll('[data-bare-size-guide]').forEach(guide => {
          guide.open = false;
          const units = guide.querySelector('[data-size-units]');
          if (!units) return;
          const showUnits = () => guide.querySelectorAll('[data-size-table]').forEach(panel => {
            panel.hidden = panel.dataset.sizeTable !== units.value;
          });
          units.addEventListener('change', showUnits); showUnits();
          guide.querySelector('[data-size-units-control]').hidden = false;
        });
        specifications.append(...details.childNodes);
      }
      status.remove(); refresh();
      if (restored) {
        const thumbnail = [...gallery.querySelectorAll('[data-native-photo]')].find(link => restored.photo && imageKey(link.href) === imageKey(restored.photo));
        if (thumbnail) showPhoto(thumbnail);
        specifications.querySelectorAll('details').forEach((detail, index) => {
          if (typeof restored.disclosures?.[index] === 'boolean') detail.open = restored.disclosures[index];
        });
        // Back starts the restored product view at its heading and photos,
        // rather than the full-page link at the bottom of the panel.
        dialog.scrollTop = 0;
      }
    } catch (error) {
      if (error.name !== 'AbortError' && active === state) {
        status.textContent = 'Product details could not load. You can still choose an option here or view the full product page.';
        fullPage.hidden = false;
      }
    }
  }
  document.addEventListener('click', event => {
    if (event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
    const link = event.target.closest('[data-product-preview], a.oh-shop-card');
    const card = link?.closest('[data-native-card], [data-demo-handle]');
    if (!card || card.hasAttribute('data-course-card') || !link.href || !card.querySelector('[data-product-preview]')) return;
    const url = new URL(link.href);
    if (url.origin !== location.origin || link.target && link.target !== '_self') return;
    event.preventDefault(); open(card, link, link.href);
  }, true);
  function restoreReturn() {
    const saved = history.state?.[returnKey];
    close();
    if (!saved || saved.source !== location.href) return;
    // Catalog cards can arrive after pageshow, or be rebuilt by a filter's
    // popstate listener. Wait until that render settles before reopening.
    let observer, timer, frame, stopped = false;
    const stop = () => {
      stopped = true; observer?.disconnect(); clearTimeout(timer); cancelAnimationFrame(frame);
      document.removeEventListener('pointerdown', stop, true);
      document.removeEventListener('keydown', stop, true);
      if (cancelRestore === stop) cancelRestore = null;
    };
    cancelRestore = stop;
    const attempt = () => {
      if (stopped) return;
      const opener = [...document.querySelectorAll('[data-product-preview]')].find(link => new URL(link.href, location.href).pathname === saved.product);
      const card = opener?.closest('[data-native-card], [data-demo-handle]');
      if (!card) return;
      stop();
      const select = card.querySelector('.oh-quick-buy select');
      if ([...(select?.options || [])].some(option => option.value === saved.variant && !option.disabled)) {
        select.value = saved.variant; select.dispatchEvent(new Event('change', {bubbles:true}));
      }
      window.scrollTo({top:saved.scroll || 0, behavior:'instant'});
      open(card, opener, opener.href, saved);
    };
    const schedule = () => {cancelAnimationFrame(frame); frame = requestAnimationFrame(() => {frame = requestAnimationFrame(attempt);});};
    observer = new MutationObserver(schedule); observer.observe(document.body, {childList:true, subtree:true});
    timer = setTimeout(stop, 10000);
    document.addEventListener('pointerdown', stop, true); document.addEventListener('keydown', stop, true);
    schedule();
  }
  window.addEventListener('popstate', restoreReturn);
  window.addEventListener('pageshow', restoreReturn);
  document.querySelectorAll('#equipment-product').forEach(setupProductGallery);
  document.addEventListener('shopify:section:load', event => event.target.querySelectorAll('#equipment-product').forEach(setupProductGallery));
})();
